"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const AMM_1 = __importDefault(require("./AMM"));
const Base_1 = __importDefault(require("./Base"));
const Chain_1 = __importDefault(require("./models/Chain"));
const Token_1 = __importDefault(require("./Token"));
const Token_2 = __importDefault(require("./models/Token"));
const L1_ERC20_Bridge__factory_1 = require("@hop-protocol/core/contracts/factories/generated/L1_ERC20_Bridge__factory");
const L1_HomeAMBNativeToErc20__factory_1 = require("@hop-protocol/core/contracts/factories/static/L1_HomeAMBNativeToErc20__factory");
const L2_AmmWrapper__factory_1 = require("@hop-protocol/core/contracts/factories/generated/L2_AmmWrapper__factory");
const L2_Bridge__factory_1 = require("@hop-protocol/core/contracts/factories/generated/L2_Bridge__factory");
const Multicall_1 = require("./Multicall");
const priceFeed_1 = require("./priceFeed");
const ethers_1 = require("ethers");
const constants_1 = require("./constants");
const WithdrawalProof_1 = require("./utils/WithdrawalProof");
const config_1 = require("./config");
const utils_1 = require("ethers/lib/utils");
const s3FileCache = {};
let s3FileCacheTimestamp = 0;
const cacheExpireMs = 1 * 60 * 1000;
/**
 * Class representing Hop bridge.
 * @namespace HopBridge
 */
class HopBridge extends Base_1.default {
    /**
     * @desc Instantiates Hop Bridge.
     * Returns a new Hop Bridge instance.
     * @param networkOrOptionsObject - L1 network name (e.g. 'mainnet', 'goerli')
     * @param signer - Ethers `Signer` for signing transactions.
     * @param token - Token symbol or model
     * @returns HopBridge SDK instance.
     * @example
     *```js
     *import { HopBridge, Chain, Token } from '@hop-protocol/sdk'
     *import { Wallet } from 'ethers'
     *
     *const signer = new Wallet(privateKey)
     *const bridge = new HopBridge('mainnet', signer, Token.USDC, Chain.Optimism, Chain.Gnosis)
     *```
     */
    constructor(networkOrOptionsObject, signer, token, chainProviders) {
        super(networkOrOptionsObject, signer, chainProviders);
        /** Default deadline for transfers */
        this.defaultDeadlineMinutes = 7 * 24 * 60; // 1 week
        this.priceFeedApiKeys = null;
        if (networkOrOptionsObject instanceof Object) {
            const options = networkOrOptionsObject;
            if (signer || token || chainProviders) {
                throw new Error('expected only single options parameter');
            }
            token = options.token;
        }
        if (token instanceof Token_1.default || token instanceof Token_2.default) {
            this.tokenSymbol = token.symbol;
        }
        else if (typeof token === 'string') {
            this.tokenSymbol = token;
        }
        if (!token) {
            throw new Error('token is required');
        }
        this.priceFeed = new priceFeed_1.PriceFeedFromS3(this.priceFeedApiKeys);
        this.doesUseAmm = this.tokenSymbol !== constants_1.CanonicalToken.HOP;
        if (this.network === constants_1.NetworkSlug.Goerli) {
            const nonAmmAssets = this.getNonAmmAssets();
            this.doesUseAmm = !nonAmmAssets.has(this.tokenSymbol);
        }
    }
    /**
     * @desc Returns hop bridge instance with signer connected. Used for adding or changing signer.
     * @param signer - Ethers `Signer` for signing transactions.
     * @returns New HopBridge SDK instance with connected signer.
     * @example
     *```js
     *import { Hop, Token } from '@hop-protocol/sdk'
     *import { Wallet } from 'ethers'
     *
     *const signer = new Wallet(privateKey)
     *let hop = new Hop()
     * // ...
     *const bridge = hop.bridge(Token.USDC).connect(signer)
     *```
     */
    connect(signer) {
        const hopBridge = new HopBridge({
            network: this.network,
            signer,
            token: this.tokenSymbol,
            chainProviders: this.chainProviders,
            baseConfigUrl: this.baseConfigUrl,
            configFileFetchEnabled: this.configFileFetchEnabled,
            blocklist: this.blocklist,
            debugTimeLogsEnabled: this.debugTimeLogsEnabled,
            debugTimeLogsCacheEnabled: this.debugTimeLogsCacheEnabled,
            debugTimeLogsCache: this.debugTimeLogsCache
        });
        // port over exiting properties
        if (this.priceFeedApiKeys) {
            hopBridge.setPriceFeedApiKeys(this.priceFeedApiKeys);
        }
        hopBridge.baseConfigUrl = this.baseConfigUrl;
        hopBridge.configFileFetchEnabled = this.configFileFetchEnabled;
        return hopBridge;
    }
    getL1Token() {
        return this.toCanonicalToken(this.tokenSymbol, this.network, Chain_1.default.Ethereum);
    }
    getCanonicalToken(chain) {
        return this.toCanonicalToken(this.tokenSymbol, this.network, chain);
    }
    getL2HopToken(chain) {
        return this.toHopToken(this.tokenSymbol, this.network, chain);
    }
    toCanonicalToken(token, network, chain) {
        token = this.toTokenModel(token);
        chain = this.toChainModel(chain);
        let { name, symbol, decimals, image } = config_1.metadata.tokens[token.canonicalSymbol];
        if (chain.equals(Chain_1.default.Gnosis) && token.symbol === constants_1.CanonicalToken.DAI) {
            symbol = constants_1.CanonicalToken.XDAI;
        }
        let address;
        if (chain.isL1) {
            address = this.getL1CanonicalTokenAddress(token.symbol, chain);
        }
        else {
            address = this.getL2CanonicalTokenAddress(token.symbol, chain);
        }
        return new Token_1.default({
            network,
            chain,
            address,
            decimals,
            symbol: symbol,
            name,
            image,
            signer: this.signer,
            chainProviders: this.chainProviders,
            baseConfigUrl: this.baseConfigUrl,
            configFileFetchEnabled: this.configFileFetchEnabled,
            blocklist: this.blocklist,
            debugTimeLogsEnabled: this.debugTimeLogsEnabled,
            debugTimeLogsCacheEnabled: this.debugTimeLogsCacheEnabled,
            debugTimeLogsCache: this.debugTimeLogsCache
        });
    }
    toHopToken(token, network, chain) {
        chain = this.toChainModel(chain);
        token = this.toTokenModel(token);
        if (chain.isL1) {
            throw new Error('Hop tokens do not exist on layer 1');
        }
        const { name, decimals, image } = config_1.metadata.tokens[token.canonicalSymbol];
        const address = this.getL2HopBridgeTokenAddress(token.symbol, chain);
        let formattedSymbol = token.canonicalSymbol;
        let formattedName = name;
        if (this.doesUseAmm) {
            formattedSymbol = `h${formattedSymbol}`;
            formattedName = `Hop ${formattedName}`;
        }
        return new Token_1.default({
            network,
            chain,
            address,
            decimals,
            symbol: formattedSymbol,
            name: formattedName,
            image,
            signer: this.signer,
            chainProviders: this.chainProviders,
            baseConfigUrl: this.baseConfigUrl,
            configFileFetchEnabled: this.configFileFetchEnabled,
            blocklist: this.blocklist,
            debugTimeLogsEnabled: this.debugTimeLogsEnabled,
            debugTimeLogsCacheEnabled: this.debugTimeLogsCacheEnabled,
            debugTimeLogsCache: this.debugTimeLogsCache
        });
    }
    /**
     * @desc Send tokens to another chain.
     * @param tokenAmount - Token amount to send denominated in smallest unit.
     * @param sourceChain - Source chain model.
     * @param destinationChain - Destination chain model.
     * @returns Ethers Transaction object.
     * @example
     *```js
     *import { Hop, Chain, Token } from '@hop-protocol/sdk'
     *
     *const hop = new Hop()
     *const bridge = hop.connect(signer).bridge(Token.USDC)
     *\// send 1 USDC token from Optimism -> Gnosis
     *const tx = await bridge.send('1000000000000000000', Chain.Optimism, Chain.Gnosis)
     *console.log(tx.hash)
     *```
     */
    async send(tokenAmount, sourceChain, destinationChain, options = {}) {
        var _a;
        sourceChain = this.toChainModel(sourceChain);
        const populatedTx = await this.populateSendTx(tokenAmount, sourceChain, destinationChain, Object.assign(Object.assign({}, options), { checkAllowance: true }));
        let balance;
        const canonicalToken = this.getCanonicalToken(sourceChain);
        if (this.isNativeToken(sourceChain)) {
            balance = await canonicalToken.getNativeTokenBalance();
        }
        else {
            balance = await canonicalToken.balanceOf();
        }
        if (balance.lt(tokenAmount)) {
            throw new Error(constants_1.Errors.NotEnoughAllowance);
        }
        const [availableLiquidity, requiredLiquidity] = await Promise.all([
            this.getFrontendAvailableLiquidity(sourceChain, destinationChain),
            this.getRequiredLiquidity(tokenAmount, sourceChain)
        ]);
        const isAvailable = availableLiquidity.gte(requiredLiquidity);
        if (!isAvailable) {
            throw new Error('Insufficient liquidity available by bonder. Try again in a few minutes');
        }
        await this.checkConnectedChain(this.signer, sourceChain);
        const recipient = (_a = options === null || options === void 0 ? void 0 : options.recipient) !== null && _a !== void 0 ? _a : await this.getSignerAddress();
        const willFail = await this.willTransferFail(sourceChain, destinationChain, recipient);
        if (willFail) {
            throw new Error('Transfer will fail at the destination. Make sure recipient can receive asset.');
        }
        return this.sendTransaction(populatedTx, sourceChain);
    }
    async populateSendTx(tokenAmount, sourceChain, destinationChain, options = {}) {
        var _a, _b;
        tokenAmount = ethers_1.BigNumber.from(tokenAmount.toString());
        if (!sourceChain) {
            sourceChain = this.sourceChain;
        }
        if (!destinationChain) {
            destinationChain = this.destinationChain;
        }
        if (!sourceChain) {
            throw new Error('source chain is required');
        }
        if (!destinationChain) {
            throw new Error('destination chain is required');
        }
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        tokenAmount = ethers_1.BigNumber.from(tokenAmount.toString());
        // L1 -> L1 or L2
        if (sourceChain.isL1) {
            // L1 -> L1
            if (destinationChain.isL1) {
                throw new Error('Cannot send from layer 1 to layer 1');
            }
            // L1 -> L2
            const [bonderAddress, relayerFee] = await Promise.all([
                this.getBonderAddress(sourceChain, destinationChain),
                (options === null || options === void 0 ? void 0 : options.relayerFee) ? Promise.resolve(options.relayerFee) : this.getTotalFee(tokenAmount, sourceChain, destinationChain)
            ]);
            return this.populateSendL1ToL2Tx({
                destinationChain: destinationChain,
                sourceChain,
                relayer: (_a = options === null || options === void 0 ? void 0 : options.relayer) !== null && _a !== void 0 ? _a : bonderAddress,
                relayerFee,
                amount: tokenAmount,
                amountOutMin: (_b = options === null || options === void 0 ? void 0 : options.amountOutMin) !== null && _b !== void 0 ? _b : 0,
                deadline: options === null || options === void 0 ? void 0 : options.deadline,
                recipient: options === null || options === void 0 ? void 0 : options.recipient,
                checkAllowance: options === null || options === void 0 ? void 0 : options.checkAllowance
            });
        }
        // else:
        // L2 -> L1 or L2
        let bonderFee = options === null || options === void 0 ? void 0 : options.bonderFee;
        if (!bonderFee) {
            bonderFee = await this.getTotalFee(tokenAmount, sourceChain, destinationChain);
        }
        // L2 -> L1
        if (destinationChain.isL1) {
            return this.populateSendL2ToL1Tx({
                destinationChain: destinationChain,
                sourceChain,
                amount: tokenAmount,
                bonderFee,
                recipient: options === null || options === void 0 ? void 0 : options.recipient,
                amountOutMin: options === null || options === void 0 ? void 0 : options.amountOutMin,
                deadline: options === null || options === void 0 ? void 0 : options.deadline,
                destinationAmountOutMin: options === null || options === void 0 ? void 0 : options.destinationAmountOutMin,
                destinationDeadline: options === null || options === void 0 ? void 0 : options.destinationDeadline,
                checkAllowance: options === null || options === void 0 ? void 0 : options.checkAllowance
            });
        }
        // L2 -> L2
        return this.populateSendL2ToL2Tx({
            destinationChain: destinationChain,
            sourceChain,
            amount: tokenAmount,
            bonderFee,
            recipient: options === null || options === void 0 ? void 0 : options.recipient,
            amountOutMin: options === null || options === void 0 ? void 0 : options.amountOutMin,
            deadline: options === null || options === void 0 ? void 0 : options.deadline,
            destinationAmountOutMin: options === null || options === void 0 ? void 0 : options.destinationAmountOutMin,
            destinationDeadline: options === null || options === void 0 ? void 0 : options.destinationDeadline,
            checkAllowance: options === null || options === void 0 ? void 0 : options.checkAllowance
        });
    }
    async estimateSendGasLimit(tokenAmount, sourceChain, destinationChain, options = {}) {
        const populatedTx = await this.populateSendTx(tokenAmount, sourceChain, destinationChain, options);
        return this.getEstimatedGasLimit(sourceChain, destinationChain, populatedTx);
    }
    async getEstimatedGasLimit(sourceChain, destinationChain, populatedTx) {
        sourceChain = this.toChainModel(sourceChain);
        if (!populatedTx.from) {
            // a `from` address is required if using only provider (not signer)
            populatedTx.from = await this.getGasEstimateFromAddress(sourceChain, destinationChain);
        }
        const provider = await this.getSignerOrProvider(sourceChain);
        try {
            return await this.estimateGas(provider, populatedTx);
        }
        catch (err) {
            console.warn('hop sdk getEstimatedGasLimit error estimating gas limit. trying fixed gasLimit for estimateGas');
            return await this.estimateGas(provider, Object.assign(Object.assign({}, populatedTx), { gasLimit: sourceChain.equals(Chain_1.default.Arbitrum) ? 1000000 : 500000 }));
        }
    }
    async getSendEstimatedGasCost(tokenAmount, sourceChain, destinationChain, options = {}) {
        sourceChain = this.toChainModel(sourceChain);
        const populatedTx = await this.populateSendTx(tokenAmount, sourceChain, destinationChain, options);
        const [estimatedGasLimit, gasPrice] = await Promise.all([
            this.getEstimatedGasLimit(sourceChain, destinationChain, populatedTx),
            this.getGasPrice(sourceChain.provider)
        ]);
        return gasPrice.mul(estimatedGasLimit);
    }
    getSendApprovalAddress(sourceChain, isHTokenTransfer = false, destinationChain // this param was later added hence it's after the boolean param for backward compatibility
    ) {
        sourceChain = this.toChainModel(sourceChain);
        if (sourceChain.equals(Chain_1.default.Ethereum)) {
            return this.getL1BridgeAddress(this.tokenSymbol, sourceChain);
        }
        const ammWrapperAddress = this.getL2AmmWrapperAddress(this.tokenSymbol, sourceChain);
        const l2BridgeAddress = this.getL2BridgeAddress(this.tokenSymbol, sourceChain);
        let approvalAddress;
        if (isHTokenTransfer || !this.doesUseAmm) {
            approvalAddress = l2BridgeAddress;
        }
        else {
            approvalAddress = ammWrapperAddress;
        }
        return approvalAddress;
    }
    async populateSendApprovalTx(tokenAmount, sourceChain, isHTokenTransfer = false, destinationChain // this param was later added hence it's after the boolean param for backward compatibility
    ) {
        sourceChain = this.toChainModel(sourceChain);
        const spender = this.getSendApprovalAddress(sourceChain, isHTokenTransfer, destinationChain);
        const isNativeToken = this.isNativeToken(sourceChain);
        if (isNativeToken) {
            return null;
        }
        let token;
        if (sourceChain.isL1) {
            token = this.getL1Token();
        }
        else if (isHTokenTransfer) {
            token = this.getL2HopToken(sourceChain);
        }
        else {
            token = this.getCanonicalToken(sourceChain);
        }
        const populatedTx = await token.populateApproveTx(spender, tokenAmount);
        return populatedTx;
    }
    async sendApproval(tokenAmount, sourceChain, destinationChain, isHTokenTransfer = false) {
        sourceChain = this.toChainModel(sourceChain);
        const populatedTx = await this.populateSendApprovalTx(tokenAmount, sourceChain, isHTokenTransfer, destinationChain);
        if (populatedTx) {
            await this.checkConnectedChain(this.signer, sourceChain);
            return this.sendTransaction(populatedTx, sourceChain);
        }
    }
    async sendHToken(tokenAmount, sourceChain, destinationChain, options = {}) {
        sourceChain = this.toChainModel(sourceChain);
        const populatedTx = await this.populateSendHTokensTx(tokenAmount, sourceChain, destinationChain, options);
        await this.checkConnectedChain(this.signer, sourceChain);
        return this.sendTransaction(populatedTx, sourceChain);
    }
    async estimateSendHTokensGasLimit(tokenAmount, sourceChain, destinationChain, options = {}) {
        const populatedTx = await this.populateSendHTokensTx(tokenAmount, sourceChain, destinationChain, options);
        return this.getEstimatedGasLimit(sourceChain, destinationChain, populatedTx);
    }
    async populateSendHTokensTx(tokenAmount, sourceChain, destinationChain, options = {}) {
        var _a;
        if (!sourceChain) {
            throw new Error('source chain is required');
        }
        if (!destinationChain) {
            throw new Error('destination chain is required');
        }
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        if (sourceChain.isL1 && destinationChain.isL1) {
            throw new Error('sourceChain and destinationChain cannot both be L1');
        }
        else if (!sourceChain.isL1 && !destinationChain.isL1) {
            throw new Error('Sending hToken L2 to L2 is not currently supported');
        }
        if ((options === null || options === void 0 ? void 0 : options.deadline) ||
            (options === null || options === void 0 ? void 0 : options.amountOutMin) ||
            (options === null || options === void 0 ? void 0 : options.destinationDeadline) ||
            (options === null || options === void 0 ? void 0 : options.destinationAmountOutMin)) {
            throw new Error('Invalid sendHToken option');
        }
        let defaultBonderFee = ethers_1.BigNumber.from(0);
        if (!sourceChain.isL1) {
            defaultBonderFee = await this.getTotalFee(tokenAmount, sourceChain, destinationChain);
        }
        let recipient = (_a = options === null || options === void 0 ? void 0 : options.recipient) !== null && _a !== void 0 ? _a : await this.getSignerAddress();
        if (!recipient) {
            throw new Error('recipient is required');
        }
        recipient = (0, utils_1.getAddress)(recipient);
        const bonderFee = (options === null || options === void 0 ? void 0 : options.bonderFee)
            ? ethers_1.BigNumber.from(options === null || options === void 0 ? void 0 : options.bonderFee)
            : defaultBonderFee;
        const amountOutMin = ethers_1.BigNumber.from(0);
        const deadline = ethers_1.BigNumber.from(0);
        const relayer = await this.getBonderAddress(sourceChain, destinationChain);
        if (!relayer) {
            throw new Error('Relayer address is required');
        }
        if (sourceChain.isL1) {
            if (bonderFee.gt(0) && !this.relayerFeeEnabled[destinationChain.slug]) {
                throw new Error('Bonder fee should be 0 when sending from L1 to L2 and relayer fee is disabled');
            }
            const l1Bridge = await this.getL1Bridge(sourceChain.provider);
            const isPaused = await l1Bridge.isChainIdPaused(destinationChain.chainId);
            if (isPaused) {
                throw new Error(`deposits to destination chain "${destinationChain.name}" are currently paused. Please check official announcement channels for status updates.`);
            }
            const isNativeToken = this.isNativeToken(sourceChain);
            const value = isNativeToken ? tokenAmount : undefined;
            if (!this.isValidRelayerAndRelayerFee(relayer, bonderFee)) {
                throw new Error('Bonder fee should be 0 when sending from L1 to L2 and relayer is not set');
            }
            const txOptions = [
                destinationChain.chainId,
                recipient,
                tokenAmount,
                amountOutMin,
                deadline,
                relayer,
                bonderFee,
                Object.assign(Object.assign({}, (await this.txOverrides(Chain_1.default.Ethereum, destinationChain))), { value })
            ];
            const tx = await l1Bridge.populateTransaction.sendToL2(...txOptions);
            return tx;
        }
        else {
            if (bonderFee.eq(0)) {
                throw new Error('Send at least the minimum Bonder fee');
            }
            const txOptions = [
                destinationChain.chainId,
                recipient,
                tokenAmount,
                bonderFee,
                amountOutMin,
                deadline,
                await this.txOverrides(sourceChain)
            ];
            const l2Bridge = await this.getL2Bridge(sourceChain, sourceChain.provider);
            return l2Bridge.populateTransaction.send(...txOptions);
        }
    }
    getTokenSymbol() {
        return this.tokenSymbol;
    }
    getTokenImage() {
        var _a;
        return (_a = this.getL1Token()) === null || _a === void 0 ? void 0 : _a.image;
    }
    getTokenDecimals() {
        const token = this.toTokenModel(this.tokenSymbol);
        return token.decimals;
    }
    async getTokenBalance(chain, address) {
        const token = this.getCanonicalToken(chain);
        return token.balanceOf(address);
    }
    async getSendData(amountIn, sourceChain, destinationChain, isHTokenSend = false) {
        amountIn = ethers_1.BigNumber.from(amountIn);
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        const amountInNoSlippage = ethers_1.BigNumber.from(1000);
        const lpFees = this.getLpFees(amountIn, sourceChain, destinationChain);
        const [hTokenAmount, amountOutNoSlippage, bonderFeeRelative, destinationTxFeeData, feeBps, availableLiquidity] = await Promise.all([
            this.calcToHTokenAmount(amountIn, sourceChain, isHTokenSend),
            this.getAmountOut(amountInNoSlippage, sourceChain, destinationChain),
            this.getBonderFeeRelative(amountIn, sourceChain, destinationChain, isHTokenSend),
            this.getDestinationTransactionFeeData(sourceChain, destinationChain),
            this.getFeeBps(this.tokenSymbol, destinationChain),
            !(sourceChain === null || sourceChain === void 0 ? void 0 : sourceChain.isL1) ? this.getFrontendAvailableLiquidity(sourceChain, destinationChain) : Promise.resolve(null)
        ]);
        const { destinationTxFee, rate: tokenPriceRate, chainNativeTokenPrice, tokenPrice, destinationChainGasPrice } = destinationTxFeeData;
        let amountOutWithoutFee;
        let adjustedBonderFee = ethers_1.BigNumber.from(0);
        let adjustedDestinationTxFee = ethers_1.BigNumber.from(0);
        let totalFee = ethers_1.BigNumber.from(0);
        if (sourceChain.isL1) {
            if (this.relayerFeeEnabled[destinationChain.slug]) {
                adjustedBonderFee = ethers_1.BigNumber.from(0);
                adjustedDestinationTxFee = destinationTxFee;
                totalFee = adjustedBonderFee.add(adjustedDestinationTxFee);
            }
            amountOutWithoutFee = await this.calcFromHTokenAmount(hTokenAmount, destinationChain);
        }
        else {
            let bonderFeeAbsolute;
            if (isHTokenSend) {
                // fees do not need to be adjusted for AMM slippage when sending hTokens
                adjustedBonderFee = bonderFeeRelative;
                adjustedDestinationTxFee = destinationTxFee;
                ([amountOutWithoutFee, bonderFeeAbsolute] = await Promise.all([
                    this.calcFromHTokenAmount(hTokenAmount, destinationChain),
                    this.getBonderFeeAbsolute(sourceChain)
                ]));
            }
            else {
                // adjusted fee is the fee in the canonical token after adjusting for the hToken price
                ;
                ([[amountOutWithoutFee, adjustedBonderFee, adjustedDestinationTxFee], bonderFeeAbsolute] = await Promise.all([
                    this.calcFromHTokenAmountMulticall(destinationChain, [hTokenAmount, bonderFeeRelative, destinationTxFee]),
                    this.getBonderFeeAbsolute(sourceChain)
                ]));
            }
            // enforce bonderFeeAbsolute after adjustment
            adjustedBonderFee = adjustedBonderFee.gt(bonderFeeAbsolute) ? adjustedBonderFee : bonderFeeAbsolute;
            totalFee = adjustedBonderFee.add(adjustedDestinationTxFee);
        }
        const sourceToken = this.getCanonicalToken(sourceChain);
        const destToken = this.getCanonicalToken(destinationChain);
        const rate = this.getRate(amountIn, amountOutWithoutFee, sourceToken, destToken);
        const marketRate = this.getRate(amountInNoSlippage, amountOutNoSlippage, sourceToken, destToken);
        const priceImpact = this.getPriceImpact(rate, marketRate);
        const relayFeeEth = ethers_1.BigNumber.from(0);
        let estimatedReceived = amountOutWithoutFee;
        if (totalFee.gt(0)) {
            estimatedReceived = estimatedReceived.sub(totalFee);
        }
        if (estimatedReceived.lt(0)) {
            estimatedReceived = ethers_1.BigNumber.from(0);
        }
        let isLiquidityAvailable = true;
        if (availableLiquidity) {
            isLiquidityAvailable = availableLiquidity.gte(hTokenAmount);
        }
        const lpFeeBps = this.getLpFeeBps(destinationChain);
        return {
            amountIn,
            sourceChain,
            destinationChain,
            isHTokenSend,
            amountOut: amountOutWithoutFee,
            rate,
            priceImpact,
            requiredLiquidity: hTokenAmount,
            lpFees,
            bonderFeeRelative,
            adjustedBonderFee,
            destinationTxFee,
            adjustedDestinationTxFee,
            totalFee,
            estimatedReceived,
            feeBps,
            lpFeeBps,
            tokenPriceRate,
            chainNativeTokenPrice,
            tokenPrice,
            destinationChainGasPrice,
            relayFeeEth,
            isLiquidityAvailable
        };
    }
    getSendDataAmountOutMins(getSendDataResponse, slippageTolerance) {
        const { sourceChain, destinationChain, requiredLiquidity, amountIn, amountOut, totalFee } = getSendDataResponse;
        const amountOutMin = this.calcAmountOutMin(amountOut, slippageTolerance);
        // l1->l2
        if (sourceChain.isL1) {
            return {
                amount: amountIn,
                amountOutMin: amountOutMin.sub(totalFee),
                destinationAmountOutMin: null,
                deadline: this.defaultDeadlineSeconds,
                destinationDeadline: null
            };
        }
        // l2->l1
        if (destinationChain.isL1) {
            return {
                amount: amountIn,
                amountOutMin: amountOutMin.sub(totalFee),
                destinationAmountOutMin: ethers_1.BigNumber.from(0),
                deadline: this.defaultDeadlineSeconds,
                destinationDeadline: 0
            };
        }
        // l2->l2
        return {
            amount: amountIn,
            amountOutMin: this.calcAmountOutMin(requiredLiquidity, slippageTolerance).sub(totalFee),
            destinationAmountOutMin: amountOutMin.sub(totalFee),
            deadline: this.defaultDeadlineSeconds,
            destinationDeadline: this.defaultDeadlineSeconds
        };
    }
    async getAmmData(chain, amountIn, isToHToken, slippageTolerance) {
        chain = this.toChainModel(chain);
        amountIn = ethers_1.BigNumber.from(amountIn);
        const canonicalToken = this.getCanonicalToken(chain);
        const hToken = this.getL2HopToken(chain);
        const sourceToken = isToHToken ? canonicalToken : hToken;
        const destToken = isToHToken ? hToken : canonicalToken;
        const amountInNoSlippage = ethers_1.BigNumber.from(1000);
        let amountOut;
        let amountOutNoSlippage;
        if (isToHToken) {
            ;
            ([amountOut, amountOutNoSlippage] = await this.calcToHTokenAmountMulticall(chain, [amountIn, amountInNoSlippage]));
        }
        else {
            ;
            ([amountOut, amountOutNoSlippage] = await this.calcFromHTokenAmountMulticall(chain, [amountIn, amountInNoSlippage]));
        }
        const rate = this.getRate(amountIn, amountOut, sourceToken, destToken);
        const marketRate = this.getRate(amountInNoSlippage, amountOutNoSlippage, sourceToken, destToken);
        const priceImpact = this.getPriceImpact(rate, marketRate);
        const oneDestBN = ethers_1.ethers.utils.parseUnits('1', sourceToken.decimals);
        const amountOutMin = this.calcAmountOutMin(amountOut, slippageTolerance);
        // Divide by 10000 at the end so that the amount isn't floored at 0
        const lpFee = this.getLpFeeBps(chain);
        const lpFeeBN = (0, utils_1.parseUnits)(lpFee.toString(), destToken.decimals);
        const lpFeeAmount = amountIn
            .mul(lpFeeBN)
            .div(oneDestBN)
            .div(10000);
        return {
            rate,
            priceImpact,
            amountOutMin,
            lpFeeAmount
        };
    }
    async getTotalFee(amountIn, sourceChain, destinationChain) {
        const { totalFee } = await this.getSendData(amountIn, sourceChain, destinationChain);
        return totalFee;
    }
    getLpFees(amountIn, sourceChain, destinationChain) {
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        let lpFeeBpsBn = ethers_1.BigNumber.from(0);
        if (!sourceChain.isL1) {
            const lpFee = this.getLpFeeBps(sourceChain);
            lpFeeBpsBn = lpFeeBpsBn.add(lpFee);
        }
        if (!destinationChain.isL1) {
            const lpFee = this.getLpFeeBps(destinationChain);
            lpFeeBpsBn = lpFeeBpsBn.add(lpFee);
        }
        amountIn = ethers_1.BigNumber.from(amountIn);
        const lpFees = amountIn.mul(lpFeeBpsBn).div(10000);
        return lpFees;
    }
    async getDestinationTransactionFee(sourceChain, destinationChain) {
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        const { destinationTxFee } = await this.getDestinationTransactionFeeData(sourceChain, destinationChain);
        return destinationTxFee;
    }
    async getDestinationTransactionFeeData(sourceChain, destinationChain) {
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        if (sourceChain.isL1 && !this.relayerFeeEnabled[destinationChain.slug]) {
            return {
                destinationTxFee: ethers_1.BigNumber.from(0),
                rate: null,
                chainNativeTokenPrice: null,
                tokenPrice: null,
                destinationChainGasPrice: null
            };
        }
        const timeStart = Date.now();
        const isRelayerFee = sourceChain.isL1 && this.relayerFeeEnabled[destinationChain.slug];
        const canonicalToken = this.getCanonicalToken(sourceChain);
        const chainNativeToken = this.getChainNativeToken(destinationChain);
        const [chainNativeTokenPrice, tokenPrice, destinationChainGasPrice, bondTransferGasLimit, l1FeeInWei, relayerFee] = await Promise.all([
            this.getPriceByTokenSymbol(chainNativeToken.symbol),
            this.getPriceByTokenSymbol(canonicalToken.symbol),
            this.getGasPrice(destinationChain.provider),
            this.estimateBondWithdrawalGasLimit(sourceChain, destinationChain),
            (destinationChain.equals(Chain_1.default.Optimism) || destinationChain.equals(Chain_1.default.Base)) ? this.getOptimismL1Fee(sourceChain, destinationChain) : Promise.resolve(ethers_1.BigNumber.from(0)),
            isRelayerFee ? this.getRelayerFee(destinationChain, this.tokenSymbol) : Promise.resolve(undefined)
        ]);
        const rate = chainNativeTokenPrice / tokenPrice;
        // Include the cost to settle an individual transfer
        const settlementGasLimitPerTx = constants_1.SettlementGasLimitPerTx[destinationChain.slug];
        if (!settlementGasLimitPerTx) {
            throw new Error(`settlementGasLimitPerTx not found for chain "${destinationChain.slug}"`);
        }
        const bondTransferGasLimitWithSettlement = bondTransferGasLimit.add(settlementGasLimitPerTx);
        const oneEth = (0, utils_1.parseEther)('1');
        let destinationTxFee = relayerFee;
        if (!destinationTxFee) {
            const rateBN = (0, utils_1.parseUnits)(rate.toFixed(canonicalToken.decimals), canonicalToken.decimals);
            const txFeeInWei = destinationChainGasPrice.mul(bondTransferGasLimitWithSettlement).add(l1FeeInWei);
            destinationTxFee = txFeeInWei.mul(rateBN).div(oneEth);
        }
        if (destinationChain.equals(Chain_1.default.Ethereum) ||
            destinationChain.equals(Chain_1.default.Optimism) ||
            destinationChain.equals(Chain_1.default.Arbitrum) ||
            destinationChain.equals(Chain_1.default.Nova) ||
            destinationChain.equals(Chain_1.default.Linea) ||
            destinationChain.equals(Chain_1.default.Base)) {
            const multiplier = (0, utils_1.parseEther)(this.getDestinationFeeGasPriceMultiplier().toString());
            if (multiplier.gt(0)) {
                destinationTxFee = destinationTxFee.mul(multiplier).div(oneEth);
            }
        }
        this.debugTimeLog('getDestinationTransactionFeeData', timeStart);
        return {
            destinationTxFee,
            rate,
            chainNativeTokenPrice,
            tokenPrice,
            destinationChainGasPrice
        };
    }
    async getOptimismL1Fee(sourceChain, destinationChain) {
        try {
            const timeStart = Date.now();
            const [gasLimit, { data, to }] = await Promise.all([
                this.estimateBondWithdrawalGasLimit(sourceChain, destinationChain),
                this.populateBondWithdrawalTx(sourceChain, destinationChain)
            ]);
            const l1FeeInWei = await this.estimateOptimismL1FeeFromData(gasLimit, data, to);
            this.debugTimeLog('getOptimismL1Fee', timeStart);
            return l1FeeInWei;
        }
        catch (err) {
            console.error(err);
            return ethers_1.BigNumber.from(0);
        }
    }
    async willTransferFail(sourceChain, destinationChain, recipient) {
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        try {
            const isDestinationNativeToken = this.isNativeToken(destinationChain);
            if (!isDestinationNativeToken) {
                return false;
            }
            if (sourceChain.isL1) {
                if (destinationChain.equals(Chain_1.default.ZkSync)) {
                    // TODO
                }
                else if (destinationChain.equals(Chain_1.default.Linea)) {
                    // TODO
                }
                else if (destinationChain.equals(Chain_1.default.ScrollZk)) {
                    // TODO
                }
                else if (destinationChain.equals(Chain_1.default.Base)) {
                    // TODO
                }
                else if (destinationChain.equals(Chain_1.default.PolygonZk)) {
                    // TODO
                }
                else {
                    const bonderAddress = await this.getBonderAddress(sourceChain, destinationChain);
                    await this.estimateGas(destinationChain.provider, {
                        value: ethers_1.BigNumber.from('1'),
                        from: bonderAddress,
                        to: recipient
                    });
                }
                return false;
            }
            else {
                const [bonderAddress, populatedTx] = await Promise.all([
                    this.getBonderAddress(sourceChain, destinationChain),
                    this.populateBondWithdrawalTx(sourceChain, destinationChain, recipient)
                ]);
                populatedTx.from = bonderAddress;
                await this.estimateGas(destinationChain.provider, populatedTx);
                return false;
            }
        }
        catch (err) {
            console.error('willTransferFail error:', err);
            return true;
        }
    }
    async estimateBondWithdrawalGasLimit(sourceChain, destinationChain) {
        destinationChain = this.toChainModel(destinationChain);
        try {
            const timeStart = Date.now();
            const populatedTx = await this.populateBondWithdrawalTx(sourceChain, destinationChain);
            const estimatedGas = await this.estimateGas(destinationChain.provider, populatedTx);
            this.debugTimeLog('estimateBondWithdrawalGasLimit', timeStart);
            return estimatedGas;
        }
        catch (err) {
            console.error(err, {
                destinationChain
            });
            let bondTransferGasLimit = constants_1.BondTransferGasLimit.Ethereum;
            if (destinationChain.equals(Chain_1.default.Optimism)) {
                bondTransferGasLimit = constants_1.BondTransferGasLimit.Optimism;
            }
            else if (destinationChain.equals(Chain_1.default.Arbitrum)) {
                bondTransferGasLimit = constants_1.BondTransferGasLimit.Arbitrum;
            }
            else if (destinationChain.equals(Chain_1.default.Nova)) {
                bondTransferGasLimit = constants_1.BondTransferGasLimit.Nova;
            }
            else if (destinationChain.equals(Chain_1.default.Base)) {
                bondTransferGasLimit = constants_1.BondTransferGasLimit.Base;
            }
            else if (destinationChain.equals(Chain_1.default.Linea)) {
                bondTransferGasLimit = constants_1.BondTransferGasLimit.Linea;
            }
            return ethers_1.BigNumber.from(bondTransferGasLimit);
        }
    }
    async populateBondWithdrawalTx(sourceChain, destinationChain, recipient) {
        destinationChain = this.toChainModel(destinationChain);
        let destinationBridge;
        if (destinationChain.isL1) {
            destinationBridge = await this.getL1Bridge();
        }
        else {
            destinationBridge = await this.getL2Bridge(destinationChain);
        }
        destinationBridge = destinationBridge.connect(destinationChain.provider);
        const bonder = await this.getBonderAddress(sourceChain, destinationChain);
        const amount = ethers_1.BigNumber.from(10);
        const amountOutMin = ethers_1.BigNumber.from(0);
        const bonderFee = ethers_1.BigNumber.from(1);
        const deadline = this.defaultDeadlineSeconds;
        const transferNonce = `0x${'0'.repeat(64)}`;
        if (!recipient) {
            recipient = `0x${'1'.repeat(40)}`;
        }
        const attemptSwap = this.shouldAttemptSwap(amountOutMin, deadline);
        if (attemptSwap && !destinationChain.isL1) {
            const payload = [
                recipient,
                amount,
                transferNonce,
                bonderFee,
                amountOutMin,
                deadline,
                {
                    from: bonder
                }
            ];
            const timeStart = Date.now();
            const populatedTx = await destinationBridge.populateTransaction.bondWithdrawalAndDistribute(...payload);
            this.debugTimeLog('populateBondWithdrawalTx', timeStart);
            return populatedTx;
        }
        else {
            const payload = [
                recipient,
                amount,
                transferNonce,
                bonderFee,
                {
                    from: bonder
                }
            ];
            const timeStart = Date.now();
            const populatedTx = destinationBridge.populateTransaction.bondWithdrawal(...payload);
            this.debugTimeLog('populateBondWithdrawalTx', timeStart);
            return populatedTx;
        }
    }
    /**
     * @desc Estimate token amount out.
     * @param tokenAmountIn - Token amount input.
     * @param sourceChain - Source chain model.
     * @param destinationChain - Destination chain model.
     * @returns Amount as BigNumber.
     * @example
     *```js
     *import { Hop, Chain } from '@hop-protocol/sdk'
     *
     *const hop = new Hop()
     *const bridge = hop.connect(signer).bridge('USDC')
     *const amountOut = await bridge.getAmountOut('1000000000000000000', Chain.Optimism, Chain.Gnosis)
     *console.log(amountOut)
     *```
     */
    async getAmountOut(tokenAmountIn, sourceChain, destinationChain) {
        tokenAmountIn = ethers_1.BigNumber.from(tokenAmountIn.toString());
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        const timeStart = Date.now();
        const hTokenAmount = await this.calcToHTokenAmount(tokenAmountIn, sourceChain);
        const amountOut = await this.calcFromHTokenAmount(hTokenAmount, destinationChain);
        this.debugTimeLog('getAmountOut', timeStart);
        return amountOut;
    }
    /**
     * @desc Estimate the bonder liquidity needed at the destination.
     * @param tokenAmountIn - Token amount input.
     * @param sourceChain - Source chain model.
     * @returns Amount as BigNumber.
     * @example
     *```js
     *import { Hop, Chain } from '@hop-protocol/sdk'
     *
     *const hop = new Hop()
     *const bridge = hop.connect(signer).bridge('USDC')
     *const requiredLiquidity = await bridge.getRequiredLiquidity('1000000000000000000', Chain.Optimism, Chain.Gnosis)
     *console.log(requiredLiquidity)
     *```
     */
    async getRequiredLiquidity(tokenAmountIn, sourceChain) {
        tokenAmountIn = ethers_1.BigNumber.from(tokenAmountIn.toString());
        sourceChain = this.toChainModel(sourceChain);
        if (sourceChain.equals(Chain_1.default.Ethereum)) {
            return ethers_1.BigNumber.from(0);
        }
        const hTokenAmount = await this.calcToHTokenAmount(tokenAmountIn, sourceChain);
        return hTokenAmount;
    }
    async getAvailableLiquidity(destinationChain, bonder) {
        const [credit, debit] = await Promise.all([
            this.getCredit(destinationChain, bonder),
            this.getTotalDebit(destinationChain, bonder)
        ]);
        const availableLiquidity = credit.sub(debit);
        return availableLiquidity;
    }
    /**
     * @desc Returns available liquidity for Hop bridge at specified chain.
     * @param sourceChain - Source chain model.
     * @param destinationChain - Destination chain model.
     * @returns Available liquidity as BigNumber.
     */
    async getFrontendAvailableLiquidity(sourceChain, destinationChain) {
        if (!(this.isSupportedAsset(sourceChain) && this.isSupportedAsset(destinationChain))) {
            return ethers_1.BigNumber.from(0);
        }
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        const token = this.toTokenModel(this.tokenSymbol);
        let [availableLiquidity, unbondedTransferRootAmount, tokenPrice] = await Promise.all([
            this.getBaseAvailableCreditIncludingVault(sourceChain, destinationChain),
            this.getUnbondedTransferRootAmount(sourceChain, destinationChain),
            this.getPriceByTokenSymbol(token.canonicalSymbol)
        ]);
        // fetch on-chain if the data is not available from worker json file
        if (availableLiquidity == null) {
            const bonder = await this.getBonderAddress(sourceChain, destinationChain);
            availableLiquidity = await this.getAvailableLiquidity(destinationChain, bonder);
        }
        if (destinationChain.isL1) {
            let pendingAmounts = ethers_1.BigNumber.from(0);
            await Promise.all(config_1.bondableChains.map(async (bondableChain) => {
                let validChain = false;
                try {
                    validChain = !!this.getL2BridgeAddress(this.tokenSymbol, bondableChain);
                }
                catch (err) { }
                if (validChain) {
                    const bondableBridge = await this.getBridgeContract(bondableChain);
                    const pendingAmount = await bondableBridge.pendingAmountForChainId(Chain_1.default.Ethereum.chainId);
                    pendingAmounts = pendingAmounts.add(pendingAmount);
                }
            }));
            const isLowLiquidityToken = constants_1.LowLiquidityTokens.includes(token.canonicalSymbol);
            const buffer = isLowLiquidityToken ? constants_1.LowLiquidityTokenBufferAmountsUsd[this.tokenSymbol] : constants_1.PendingAmountBufferUsd === null || constants_1.PendingAmountBufferUsd === void 0 ? void 0 : constants_1.PendingAmountBufferUsd.toString();
            const tokenPriceBn = (0, utils_1.parseUnits)(tokenPrice.toString(), token.decimals);
            const bufferAmountBn = (0, utils_1.parseUnits)(buffer, token.decimals);
            const precision = (0, utils_1.parseUnits)('1', token.decimals);
            const bufferAmountTokensBn = bufferAmountBn.div(tokenPriceBn).mul(precision);
            availableLiquidity = availableLiquidity
                .sub(pendingAmounts)
                .sub(unbondedTransferRootAmount)
                .sub(bufferAmountTokensBn);
            if (this.isOruToL1(sourceChain, destinationChain)) {
                availableLiquidity = availableLiquidity.div(2);
            }
        }
        if (availableLiquidity.lt(0)) {
            return ethers_1.BigNumber.from(0);
        }
        return availableLiquidity;
    }
    isOruToL1(sourceChain, destinationChain) {
        return destinationChain.isL1 && config_1.bondableChains.includes(sourceChain.slug);
    }
    async getBonderAvailableLiquidityData() {
        const cached = s3FileCache[this.network];
        const isExpired = s3FileCacheTimestamp + cacheExpireMs < Date.now();
        if (cached && !isExpired) {
            return cached;
        }
        const data = await this.fetchBonderAvailableLiquidityDataWithIpfsFallback();
        if (data) {
            s3FileCache[this.network] = data;
            s3FileCacheTimestamp = Date.now();
        }
        return data;
    }
    async getUnbondedTransferRootAmount(sourceChain, destinationChain) {
        var _a, _b;
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        try {
            const data = await this.getBonderAvailableLiquidityData();
            if (data) {
                const tokenData = data === null || data === void 0 ? void 0 : data[this.tokenSymbol];
                const _unbondedTransferRootAmount = (_b = (_a = tokenData === null || tokenData === void 0 ? void 0 : tokenData.unbondedTransferRootAmounts) === null || _a === void 0 ? void 0 : _a[sourceChain.slug]) === null || _b === void 0 ? void 0 : _b[destinationChain.slug];
                if (_unbondedTransferRootAmount) {
                    return ethers_1.BigNumber.from(_unbondedTransferRootAmount);
                }
            }
        }
        catch (err) {
            console.error(err);
        }
        return ethers_1.BigNumber.from(0);
    }
    async getBaseAvailableCreditIncludingVault(sourceChain, destinationChain) {
        var _a, _b;
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        try {
            const data = await this.getBonderAvailableLiquidityData();
            if (data) {
                const tokenData = data === null || data === void 0 ? void 0 : data[this.tokenSymbol];
                const _baseAvailableCreditIncludingVault = (_b = (_a = tokenData === null || tokenData === void 0 ? void 0 : tokenData.baseAvailableCreditIncludingVault) === null || _a === void 0 ? void 0 : _a[sourceChain.slug]) === null || _b === void 0 ? void 0 : _b[destinationChain.slug];
                if (_baseAvailableCreditIncludingVault) {
                    return ethers_1.BigNumber.from(_baseAvailableCreditIncludingVault);
                }
            }
        }
        catch (err) {
            console.error(err);
        }
    }
    async getVaultBalance(destinationChain, bonder) {
        var _a, _b;
        destinationChain = this.toChainModel(destinationChain);
        try {
            const data = await this.getBonderAvailableLiquidityData();
            if (data) {
                const tokenData = data === null || data === void 0 ? void 0 : data[this.tokenSymbol];
                const _vaultBalance = (_b = (_a = tokenData === null || tokenData === void 0 ? void 0 : tokenData.bonderVaultBalance) === null || _a === void 0 ? void 0 : _a[bonder]) === null || _b === void 0 ? void 0 : _b[destinationChain.slug];
                if (_vaultBalance) {
                    return ethers_1.BigNumber.from(_vaultBalance);
                }
            }
        }
        catch (err) {
            console.error(err);
        }
        return ethers_1.BigNumber.from(0);
    }
    /**
     * @desc Returns bridge contract instance for specified chain.
     * @param chain - chain model.
     * @returns Ethers contract instance.
     */
    async getBridgeContract(chain) {
        chain = this.toChainModel(chain);
        return chain.isL1 ? this.getL1Bridge() : this.getL2Bridge(chain);
    }
    /**
     * @desc Returns total credit that bonder holds on Hop bridge at specified chain.
     * @param sourceChain - Chain model.
     * @returns Total credit as BigNumber.
     */
    async getCredit(sourceChain, bonder) {
        const bridge = await this.getBridgeContract(sourceChain);
        return bridge.getCredit(bonder);
    }
    /**
     * @desc Returns total debit, including sliding window debit, that bonder holds on Hop bridge at specified chain.
     * @param sourceChain - Chain model.
     * @returns Total debit as BigNumber.
     */
    async getTotalDebit(sourceChain, bonder) {
        const bridge = await this.getBridgeContract(sourceChain);
        return bridge.getDebitAndAdditionalDebit(bonder);
    }
    /**
     * @desc Returns total debit that bonder holds on Hop bridge at specified chain.
     * @param sourceChain - Chain model.
     * @returns Total debit as BigNumber.
     */
    async getDebit(sourceChain, bonder) {
        const bridge = await this.getBridgeContract(sourceChain);
        return bridge.getRawDebit(bonder);
    }
    /**
     * @desc Sends transaction to execute swap on Saddle contract.
     * @param sourceChain - Source chain model.
     * @param toHop - Converts to Hop token only if set to true.
     * @param amount - Amount of token to swap.
     * @param minAmountOut - Minimum amount of tokens to receive in order
     * for transaction to be successful.
     * @param deadline - Transaction deadline in seconds.
     * @returns Ethers transaction object.
     */
    async execSaddleSwap(sourceChain, toHop, amount, minAmountOut, deadline) {
        sourceChain = this.toChainModel(sourceChain);
        let tokenIndexFrom;
        let tokenIndexTo;
        const l2CanonicalTokenAddress = this.getL2CanonicalTokenAddress(this.tokenSymbol, sourceChain);
        if (!l2CanonicalTokenAddress) {
            throw new Error(`source chain "${sourceChain.slug}" is unsupported`);
        }
        const l2HopBridgeTokenAddress = this.getL2HopBridgeTokenAddress(this.tokenSymbol, sourceChain);
        if (!l2HopBridgeTokenAddress) {
            throw new Error(`source chain "${sourceChain.slug}" is unsupported`);
        }
        const amm = this.getAmm(sourceChain);
        const saddleSwap = await amm.getSaddleSwap();
        const [canonicalTokenIndex, hopTokenIndex] = await Promise.all([
            saddleSwap.getTokenIndex(l2CanonicalTokenAddress).then((index) => Number(index.toString())),
            saddleSwap.getTokenIndex(l2HopBridgeTokenAddress).then((index) => Number(index.toString()))
        ]);
        if (toHop) {
            tokenIndexFrom = canonicalTokenIndex;
            tokenIndexTo = hopTokenIndex;
        }
        else {
            tokenIndexFrom = hopTokenIndex;
            tokenIndexTo = canonicalTokenIndex;
        }
        return saddleSwap.swap(tokenIndexFrom, tokenIndexTo, amount, minAmountOut, deadline);
    }
    /**
     * @desc Returns Hop L1 Bridge Ethers contract instance.
     * @param signer - Ethers signer
     * @returns Ethers contract instance.
     */
    async getL1Bridge(signer = this.signer) {
        const bridgeAddress = this.getL1BridgeAddress(this.tokenSymbol, Chain_1.default.Ethereum);
        if (!bridgeAddress) {
            throw new Error(`token "${this.tokenSymbol}" is unsupported`);
        }
        const provider = await this.getSignerOrProvider(Chain_1.default.Ethereum, signer);
        return L1_ERC20_Bridge__factory_1.L1_ERC20_Bridge__factory.connect(bridgeAddress, provider);
    }
    /**
     * @desc Returns Hop L2 Bridge Ethers contract instance.
     * @param chain - Chain model.
     * @param signer - Ethers signer
     * @returns Ethers contract instance.
     */
    async getL2Bridge(chain, signer = this.signer) {
        chain = this.toChainModel(chain);
        const bridgeAddress = this.getL2BridgeAddress(this.tokenSymbol, chain);
        if (!bridgeAddress) {
            throw new Error(`token "${this.tokenSymbol}" on chain "${chain.slug}" is unsupported`);
        }
        const provider = await this.getSignerOrProvider(chain, signer);
        return L2_Bridge__factory_1.L2_Bridge__factory.connect(bridgeAddress, provider);
    }
    getAmm(chain) {
        chain = this.toChainModel(chain);
        if (chain.isL1) {
            throw new Error('No AMM exists on L1');
        }
        return new AMM_1.default({
            network: this.network,
            tokenSymbol: this.tokenSymbol,
            chain,
            signer: this.signer,
            chainProviders: this.chainProviders,
            baseConfigUrl: this.baseConfigUrl,
            configFileFetchEnabled: this.configFileFetchEnabled,
            blocklist: this.blocklist,
            debugTimeLogsEnabled: this.debugTimeLogsEnabled,
            debugTimeLogsCacheEnabled: this.debugTimeLogsCacheEnabled,
            debugTimeLogsCache: this.debugTimeLogsCache
        });
    }
    /**
     * @desc Returns Hop Bridge AMM wrapper Ethers contract instance.
     * @param chain - Chain model.
     * @param signer - Ethers signer
     * @returns Ethers contract instance.
     */
    async getAmmWrapper(chain, signer = this.signer) {
        chain = this.toChainModel(chain);
        const ammWrapperAddress = this.getL2AmmWrapperAddress(this.tokenSymbol, chain);
        if (!ammWrapperAddress) {
            throw new Error(`token "${this.tokenSymbol}" on chain "${chain.slug}" is unsupported`);
        }
        const provider = await this.getSignerOrProvider(chain, signer);
        return L2_AmmWrapper__factory_1.L2_AmmWrapper__factory.connect(ammWrapperAddress, provider);
    }
    /**
     * @desc Returns Hop Bridge Saddle reserve amounts.
     * @param chain - Chain model.
     * @returns Array containing reserve amounts for canonical token
     * and hTokens.
     */
    async getSaddleSwapReserves(chain = this.sourceChain) {
        const amm = this.getAmm(chain);
        const saddleSwap = await amm.getSaddleSwap();
        return Promise.all([
            saddleSwap.getTokenBalance(0),
            saddleSwap.getTokenBalance(1)
        ]);
    }
    async getReservesTotal(chain = this.sourceChain) {
        const [reserve0, reserve1] = await this.getSaddleSwapReserves(chain);
        return reserve0.add(reserve1);
    }
    async getTvl(chain = this.sourceChain) {
        return this.getReservesTotal(chain);
    }
    async getTvlUsd(chain = this.sourceChain) {
        const token = this.toTokenModel(this.tokenSymbol);
        const [tvl, tokenPrice] = await Promise.all([
            this.getTvl(chain),
            this.getPriceByTokenSymbol(token.canonicalSymbol)
        ]);
        if (tvl.lte(0)) {
            return 0;
        }
        const tvlFormatted = this.formatUnits(tvl);
        let tvlUsd = tvlFormatted * tokenPrice;
        if (tvlUsd < 0) {
            tvlUsd = 0;
        }
        return tvlUsd;
    }
    /**
     * @desc Returns Hop Bridge Saddle Swap LP Token Ethers contract instance.
     * @param chain - Chain model.
     * @param signer - Ethers signer
     * @returns Ethers contract instance.
     */
    getSaddleLpToken(chain, signer = this.signer) {
        // ToDo: Remove ability to pass in signer like other token getters
        chain = this.toChainModel(chain);
        const saddleLpTokenAddress = this.getL2SaddleLpTokenAddress(this.tokenSymbol, chain);
        if (!saddleLpTokenAddress) {
            throw new Error(`token "${this.tokenSymbol}" on chain "${chain.slug}" is unsupported`);
        }
        // ToDo: Get actual saddle LP token symbol and name
        return new Token_1.default({
            network: this.network,
            chain,
            address: saddleLpTokenAddress,
            decimals: 18,
            symbol: `${this.tokenSymbol} LP`,
            name: `${this.tokenSymbol} LP`,
            image: '',
            signer,
            chainProviders: this.chainProviders,
            baseConfigUrl: this.baseConfigUrl,
            configFileFetchEnabled: this.configFileFetchEnabled,
            blocklist: this.blocklist,
            debugTimeLogsEnabled: this.debugTimeLogsEnabled,
            debugTimeLogsCacheEnabled: this.debugTimeLogsCacheEnabled,
            debugTimeLogsCache: this.debugTimeLogsCache
        });
    }
    /**
     * @desc Sends transaction to add liquidity to AMM.
     * @param amount0Desired - Amount of token #0 in smallest unit
     * @param amount1Desired - Amount of token #1 in smallest unit
     * @param chain - Chain model of desired chain to add liquidity to.
     * @param options - Method options.
     * @returns Ethers transaction object.
     */
    async addLiquidity(amount0Desired, amount1Desired, chain, options = {}) {
        if (!chain) {
            chain = this.sourceChain;
        }
        amount0Desired = ethers_1.BigNumber.from(amount0Desired.toString());
        chain = this.toChainModel(chain);
        const amm = new AMM_1.default({
            network: this.network,
            tokenSymbol: this.tokenSymbol,
            chain,
            signer: this.signer,
            chainProviders: this.chainProviders,
            baseConfigUrl: this.baseConfigUrl,
            configFileFetchEnabled: this.configFileFetchEnabled,
            blocklist: this.blocklist,
            debugTimeLogsEnabled: this.debugTimeLogsEnabled,
            debugTimeLogsCacheEnabled: this.debugTimeLogsCacheEnabled,
            debugTimeLogsCache: this.debugTimeLogsCache
        });
        return amm.addLiquidity(amount0Desired, amount1Desired, options.minToMint, options.deadline);
    }
    /**
     * @desc Sends transaction to remove liquidity from AMM.
     * @param liquidityTokenAmount - Amount of LP tokens to burn.
     * @param chain - Chain model of desired chain to add liquidity to.
     * @param options - Method options.
     * @returns Ethers transaction object.
     */
    async removeLiquidity(liquidityTokenAmount, chain, options = {}) {
        if (!chain) {
            chain = this.sourceChain;
        }
        chain = this.toChainModel(chain);
        const amm = new AMM_1.default({
            network: this.network,
            tokenSymbol: this.tokenSymbol,
            chain,
            signer: this.signer,
            chainProviders: this.chainProviders,
            baseConfigUrl: this.baseConfigUrl,
            configFileFetchEnabled: this.configFileFetchEnabled,
            blocklist: this.blocklist,
            debugTimeLogsEnabled: this.debugTimeLogsEnabled,
            debugTimeLogsCacheEnabled: this.debugTimeLogsCacheEnabled,
            debugTimeLogsCache: this.debugTimeLogsCache
        });
        return amm.removeLiquidity(liquidityTokenAmount, options.amount0Min, options.amount1Min, options.deadline);
    }
    async removeLiquidityOneToken(lpTokenAmount, tokenIndex, chain, options = {}) {
        if (!chain) {
            chain = this.sourceChain;
        }
        chain = this.toChainModel(chain);
        const amm = new AMM_1.default({
            network: this.network,
            tokenSymbol: this.tokenSymbol,
            chain,
            signer: this.signer,
            chainProviders: this.chainProviders,
            baseConfigUrl: this.baseConfigUrl,
            configFileFetchEnabled: this.configFileFetchEnabled,
            blocklist: this.blocklist,
            debugTimeLogsEnabled: this.debugTimeLogsEnabled,
            debugTimeLogsCacheEnabled: this.debugTimeLogsCacheEnabled,
            debugTimeLogsCache: this.debugTimeLogsCache
        });
        return amm.removeLiquidityOneToken(lpTokenAmount, tokenIndex, options === null || options === void 0 ? void 0 : options.amountMin, options === null || options === void 0 ? void 0 : options.deadline);
    }
    async removeLiquidityImbalance(token0Amount, token1Amount, chain, options = {}) {
        if (!chain) {
            chain = this.sourceChain;
        }
        chain = this.toChainModel(chain);
        const amm = new AMM_1.default({
            network: this.network,
            tokenSymbol: this.tokenSymbol,
            chain,
            signer: this.signer,
            chainProviders: this.chainProviders,
            baseConfigUrl: this.baseConfigUrl,
            configFileFetchEnabled: this.configFileFetchEnabled,
            blocklist: this.blocklist,
            debugTimeLogsEnabled: this.debugTimeLogsEnabled,
            debugTimeLogsCacheEnabled: this.debugTimeLogsCacheEnabled,
            debugTimeLogsCache: this.debugTimeLogsCache
        });
        return amm.removeLiquidityImbalance(token0Amount, token1Amount, options === null || options === void 0 ? void 0 : options.maxBurnAmount, options === null || options === void 0 ? void 0 : options.deadline);
    }
    async calculateWithdrawOneToken(tokenAmount, tokenIndex, chain) {
        if (!chain) {
            chain = this.sourceChain;
        }
        chain = this.toChainModel(chain);
        const amm = new AMM_1.default({
            network: this.network,
            tokenSymbol: this.tokenSymbol,
            chain,
            signer: this.signer,
            chainProviders: this.chainProviders,
            baseConfigUrl: this.baseConfigUrl,
            configFileFetchEnabled: this.configFileFetchEnabled,
            blocklist: this.blocklist,
            debugTimeLogsEnabled: this.debugTimeLogsEnabled,
            debugTimeLogsCacheEnabled: this.debugTimeLogsCacheEnabled,
            debugTimeLogsCache: this.debugTimeLogsCache
        });
        return amm.calculateRemoveLiquidityOneToken(tokenAmount, tokenIndex);
    }
    /**
     * @readonly
     * @desc The default deadline to use in seconds.
     * @returns Deadline in seconds
     */
    get defaultDeadlineSeconds() {
        return (Date.now() / 1000 + this.defaultDeadlineMinutes * 60) | 0;
    }
    /**
     * @readonly
     * @desc The time slot for the current time.
     * @param {Object} time - Unix timestamp (in seconds) to get the time slot.
     * @returns {Object} Time slot for the given time as BigNumber.
     */
    async getTimeSlot(time) {
        const bridge = await this.getL1Bridge();
        time = ethers_1.BigNumber.from(time.toString());
        return bridge.getTimeSlot(time);
    }
    /**
     * @readonly
     * @desc The challenge period.
     * @returns {Object} The challenge period for the bridge as BigNumber.
     */
    async challengePeriod() {
        const bridge = await this.getL1Bridge();
        return bridge.challengePeriod();
    }
    /**
     * @readonly
     * @desc The size of the time slots.
     * @returns {Object} The size of the time slots for the bridge as BigNumber.
     */
    async timeSlotSize() {
        const bridge = await this.getL1Bridge();
        return bridge.TIME_SLOT_SIZE();
    }
    /**
     * @readonly
     * @desc The amount bonded for a time slot for a bonder.
     * @param timeSlot - Time slot to get.
     * @param bonder - Address of the bonder to check.
     * @returns Amount bonded for the bonder for the given time slot as BigNumber.
     */
    async timeSlotToAmountBonded(timeSlot, bonder) {
        const bridge = await this.getL1Bridge();
        timeSlot = ethers_1.BigNumber.from(timeSlot.toString());
        return bridge.timeSlotToAmountBonded(timeSlot, bonder);
    }
    async getTokenIndexes(path, chain) {
        const amm = this.getAmm(chain);
        const saddleSwap = await amm.getSaddleSwap();
        const [tokenIndexFrom, tokenIndexTo] = await Promise.all([
            saddleSwap.getTokenIndex(path[0]).then((index) => Number(index.toString())),
            saddleSwap.getTokenIndex(path[1]).then((index) => Number(index.toString()))
        ]);
        return [tokenIndexFrom, tokenIndexTo];
    }
    async populateSendL1ToL2Tx(input) {
        let { destinationChain, sourceChain, relayer, relayerFee, amount, amountOutMin, deadline, recipient, checkAllowance } = input;
        if (!sourceChain.isL1) {
            // ToDo: Don't pass in sourceChain since it will always be L1
            throw new Error('sourceChain must be L1');
        }
        if (await this.getIsBridgeDeprecated(this.tokenSymbol)) {
            throw new Error('This bridge is deprecated');
        }
        if (!relayer) {
            throw new Error('relayer is required');
        }
        const destinationChainId = destinationChain.chainId;
        deadline = deadline === undefined ? this.defaultDeadlineSeconds : deadline;
        amountOutMin = ethers_1.BigNumber.from((amountOutMin || 0).toString());
        recipient = recipient || await this.getSignerAddress();
        if (!recipient) {
            throw new Error('recipient is required');
        }
        recipient = (0, utils_1.getAddress)(recipient);
        const isNativeToken = this.isNativeToken(sourceChain);
        let l1Bridge = await this.getL1Bridge(sourceChain.provider);
        if (checkAllowance) {
            await this.checkConnectedChain(this.signer, sourceChain);
            l1Bridge = await this.getL1Bridge(this.signer);
            const spender = l1Bridge.address;
            if (!isNativeToken) {
                const l1Token = this.getL1Token();
                const allowance = await l1Token.allowance(spender);
                if (allowance.lt(ethers_1.BigNumber.from(amount))) {
                    throw new Error(constants_1.Errors.NotEnoughAllowance);
                }
            }
        }
        if (amountOutMin.lt(0)) {
            amountOutMin = ethers_1.BigNumber.from(0);
        }
        const value = isNativeToken ? amount : undefined;
        relayerFee = ethers_1.BigNumber.from(relayerFee || 0);
        if (!this.isValidRelayerAndRelayerFee(relayer, relayerFee)) {
            throw new Error('Bonder fee should be 0 when sending from L1 to L2 and relayer is not set');
        }
        const isPaused = await l1Bridge.isChainIdPaused(destinationChain.chainId);
        if (isPaused) {
            throw new Error(`deposits to destination chain "${destinationChain.name}" are currently paused. Please check official announcement channels for status updates.`);
        }
        const txOptions = [
            destinationChainId,
            recipient,
            amount || 0,
            amountOutMin,
            deadline,
            relayer,
            relayerFee,
            Object.assign(Object.assign({}, (await this.txOverrides(Chain_1.default.Ethereum, destinationChain))), { value })
        ];
        const tx = await l1Bridge.populateTransaction.sendToL2(...txOptions);
        return tx;
    }
    async populateSendL2ToL1Tx(input) {
        let { destinationChain, sourceChain, amount, bonderFee, recipient, amountOutMin, deadline, checkAllowance } = input;
        const destinationChainId = destinationChain.chainId;
        deadline = deadline === undefined ? this.defaultDeadlineSeconds : deadline;
        amountOutMin = ethers_1.BigNumber.from((amountOutMin || 0).toString());
        // Destination values will always be 0 going to L1
        const destinationDeadline = ethers_1.BigNumber.from(0);
        const destinationAmountOutMin = ethers_1.BigNumber.from(0);
        if (!destinationChain.isL1) {
            throw new Error('All transfers populated here must be sent to L1');
        }
        recipient = recipient || await this.getSignerAddress();
        if (!recipient) {
            throw new Error('recipient is required');
        }
        recipient = (0, utils_1.getAddress)(recipient);
        const ammWrapper = await this.getAmmWrapper(sourceChain, sourceChain.provider);
        const l2Bridge = await this.getL2Bridge(sourceChain, sourceChain.provider);
        const attemptSwapAtSource = this.shouldAttemptSwap(amountOutMin, deadline);
        const spender = attemptSwapAtSource ? ammWrapper.address : l2Bridge.address;
        if (ethers_1.BigNumber.from(bonderFee).gt(amount)) {
            throw new Error(`amount must be greater than bonder fee. amount: ${amount.toString()}, bonderFee: ${bonderFee.toString()}`);
        }
        const isNativeToken = this.isNativeToken(sourceChain);
        if (checkAllowance) {
            await this.checkConnectedChain(this.signer, sourceChain);
            if (!isNativeToken) {
                const l2CanonicalToken = this.getCanonicalToken(sourceChain);
                const allowance = await l2CanonicalToken.allowance(spender);
                if (allowance.lt(ethers_1.BigNumber.from(amount))) {
                    throw new Error(constants_1.Errors.NotEnoughAllowance);
                }
            }
        }
        if (amountOutMin.lt(0)) {
            amountOutMin = ethers_1.BigNumber.from(0);
        }
        const txOptions = [
            destinationChainId,
            recipient,
            amount,
            bonderFee
        ];
        if (attemptSwapAtSource) {
            const additionalOptions = [
                amountOutMin,
                deadline,
                destinationAmountOutMin,
                destinationDeadline,
                Object.assign(Object.assign({}, (await this.txOverrides(sourceChain))), { value: isNativeToken ? amount : undefined })
            ];
            return ammWrapper.populateTransaction.swapAndSend(...txOptions, ...additionalOptions);
        }
        const additionalOptions = [
            destinationAmountOutMin,
            destinationDeadline,
            Object.assign(Object.assign({}, (await this.txOverrides(sourceChain))), { value: isNativeToken ? amount : undefined })
        ];
        return l2Bridge.populateTransaction.send(...txOptions, ...additionalOptions);
    }
    async populateSendL2ToL2Tx(input) {
        let { destinationChain, sourceChain, amount, destinationAmountOutMin, bonderFee, deadline, destinationDeadline, amountOutMin, recipient, checkAllowance } = input;
        const destinationChainId = destinationChain.chainId;
        deadline = deadline || this.defaultDeadlineSeconds;
        destinationDeadline = destinationDeadline || this.defaultDeadlineSeconds;
        amountOutMin = ethers_1.BigNumber.from((amountOutMin || 0).toString());
        destinationAmountOutMin = ethers_1.BigNumber.from((destinationAmountOutMin || 0).toString());
        if (ethers_1.BigNumber.from(bonderFee).gt(amount)) {
            throw new Error('Amount must be greater than bonder fee');
        }
        recipient = recipient || await this.getSignerAddress();
        if (!recipient) {
            throw new Error('recipient is required');
        }
        recipient = (0, utils_1.getAddress)(recipient);
        const ammWrapper = await this.getAmmWrapper(sourceChain, sourceChain.provider);
        const l2Bridge = await this.getL2Bridge(sourceChain, sourceChain.provider);
        const attemptSwapAtSource = this.shouldAttemptSwap(amountOutMin, deadline);
        const spender = attemptSwapAtSource ? ammWrapper.address : l2Bridge.address;
        const isNativeToken = this.isNativeToken(sourceChain);
        if (checkAllowance) {
            await this.checkConnectedChain(this.signer, sourceChain);
            if (!isNativeToken) {
                const l2CanonicalToken = this.getCanonicalToken(sourceChain);
                const allowance = await l2CanonicalToken.allowance(spender);
                if (allowance.lt(ethers_1.BigNumber.from(amount))) {
                    throw new Error(constants_1.Errors.NotEnoughAllowance);
                }
            }
        }
        if (amountOutMin.lt(0)) {
            amountOutMin = ethers_1.BigNumber.from(0);
        }
        if (destinationAmountOutMin.lt(0)) {
            destinationAmountOutMin = ethers_1.BigNumber.from(0);
        }
        const txOptions = [
            destinationChainId,
            recipient,
            amount,
            bonderFee
        ];
        if (attemptSwapAtSource) {
            const additionalOptions = [
                amountOutMin,
                deadline,
                destinationAmountOutMin,
                destinationDeadline,
                Object.assign(Object.assign({}, (await this.txOverrides(sourceChain))), { value: isNativeToken ? amount : undefined })
            ];
            return ammWrapper.populateTransaction.swapAndSend(...txOptions, ...additionalOptions);
        }
        const additionalOptions = [
            destinationAmountOutMin,
            destinationDeadline,
            Object.assign(Object.assign({}, (await this.txOverrides(sourceChain))), { value: isNativeToken ? amount : undefined })
        ];
        return l2Bridge.populateTransaction.send(...txOptions, ...additionalOptions);
    }
    async calcToHTokenAmount(amount, chain, isHTokenSend = false) {
        if (!this.doesUseAmm || isHTokenSend) {
            return ethers_1.BigNumber.from(amount);
        }
        amount = ethers_1.BigNumber.from(amount.toString());
        if (chain.isL1) {
            return amount;
        }
        if (amount.eq(0)) {
            return ethers_1.BigNumber.from(0);
        }
        const amm = this.getAmm(chain);
        const timeStart = Date.now();
        const amountOut = await amm.calculateSwap(constants_1.TokenIndex.CanonicalToken, constants_1.TokenIndex.HopBridgeToken, amount);
        this.debugTimeLog('calcToHTokenAmount', timeStart);
        return amountOut;
    }
    async calcFromHTokenAmount(amount, chain) {
        if (!this.doesUseAmm) {
            return ethers_1.BigNumber.from(amount);
        }
        amount = ethers_1.BigNumber.from(amount.toString());
        if (chain.isL1) {
            return ethers_1.BigNumber.from(amount);
        }
        if (amount.eq(0)) {
            return ethers_1.BigNumber.from(0);
        }
        const amm = this.getAmm(chain);
        const timeStart = Date.now();
        const amountOut = await amm.calculateSwap(constants_1.TokenIndex.HopBridgeToken, constants_1.TokenIndex.CanonicalToken, amount);
        this.debugTimeLog('calcFromHTokenAmount', timeStart);
        return amountOut;
    }
    async calcSwapAmountMulticall(chain, tokenIndexes, amountIns) {
        chain = this.toChainModel(chain);
        if (!this.doesUseAmm) {
            return amountIns.map((amount) => ethers_1.BigNumber.from(amount));
        }
        if (chain.isL1) {
            return amountIns.map((amount) => ethers_1.BigNumber.from(amount));
        }
        const multicall = new Multicall_1.Multicall({ network: this.network });
        const amm = this.getAmm(chain);
        const saddleSwap = await amm.getSaddleSwap();
        const options = amountIns.map((amountIn, index) => {
            return {
                skip: ethers_1.BigNumber.from(amountIn).eq(0),
                address: saddleSwap.address,
                abi: saddleSwap.interface.fragments,
                method: 'calculateSwap',
                args: [tokenIndexes[0], tokenIndexes[1], amountIn],
                index
            };
        });
        const result = await multicall.multicall(chain.slug, options.filter((option) => !option.skip));
        const items = result.map((values) => ethers_1.BigNumber.from(values[0]));
        const skipped = options.filter((option) => option.skip);
        skipped.sort((a, b) => a.index - b.index);
        for (const skip of skipped) {
            items.splice(skip.index, 0, ethers_1.BigNumber.from(0));
        }
        return items;
    }
    async calcToHTokenAmountMulticall(chain, amountIns) {
        return this.calcSwapAmountMulticall(chain, [constants_1.TokenIndex.CanonicalToken, constants_1.TokenIndex.HopBridgeToken], amountIns);
    }
    async calcFromHTokenAmountMulticall(chain, amountIns) {
        return this.calcSwapAmountMulticall(chain, [constants_1.TokenIndex.HopBridgeToken, constants_1.TokenIndex.CanonicalToken], amountIns);
    }
    async getBonderFeeRelative(amountIn, sourceChain, destinationChain, isHTokenSend = false) {
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        if (sourceChain.isL1) {
            // Relayer fees are handled in the destination fee
            return ethers_1.BigNumber.from(0);
        }
        const timeStart = Date.now();
        const [hTokenAmount, feeBps] = await Promise.all([
            this.calcToHTokenAmount(amountIn, sourceChain, isHTokenSend),
            this.getFeeBps(this.tokenSymbol, destinationChain)
        ]);
        this.debugTimeLog('getBonderFeeRelative', timeStart);
        const bonderFeeRelative = hTokenAmount.mul(feeBps).div(10000);
        return bonderFeeRelative;
    }
    async getBonderFeeAbsolute(sourceChain) {
        sourceChain = this.toChainModel(sourceChain);
        const token = this.toTokenModel(this.tokenSymbol);
        if (sourceChain.isL1) {
            // Bonder fees are not relevant on L2
            return ethers_1.BigNumber.from(0);
        }
        const timeStart = Date.now();
        let onChainBonderFeeAbsolutePromise;
        if (token.canonicalSymbol === Token_2.default.ETH) {
            if (Chain_1.default.Gnosis.equals(sourceChain) || Chain_1.default.Polygon.equals(sourceChain)) {
                const l2Bridge = await this.getL2Bridge(sourceChain);
                onChainBonderFeeAbsolutePromise = l2Bridge.minBonderFeeAbsolute();
            }
        }
        if (this.network !== constants_1.NetworkSlug.Mainnet) {
            const l2Bridge = await this.getL2Bridge(sourceChain);
            onChainBonderFeeAbsolutePromise = l2Bridge.minBonderFeeAbsolute();
        }
        const [tokenPrice, onChainBonderFeeAbsolute] = await Promise.all([
            this.getPriceByTokenSymbol(token.canonicalSymbol),
            onChainBonderFeeAbsolutePromise !== null && onChainBonderFeeAbsolutePromise !== void 0 ? onChainBonderFeeAbsolutePromise : Promise.resolve(ethers_1.BigNumber.from(0))
        ]);
        this.debugTimeLog('getBonderFeeAbsolute', timeStart);
        const minBonderFeeUsd = 0.25;
        const minBonderFeeAbsolute = (0, utils_1.parseUnits)((minBonderFeeUsd / tokenPrice).toFixed(token.decimals), token.decimals);
        const absoluteFee = onChainBonderFeeAbsolute.gt(minBonderFeeAbsolute) ? onChainBonderFeeAbsolute : minBonderFeeAbsolute;
        return absoluteFee;
    }
    getRate(amountIn, amountOut, sourceToken, destToken) {
        let rateBN;
        if (amountIn.eq(0)) {
            rateBN = ethers_1.BigNumber.from(0);
        }
        else {
            const oneSourceBN = ethers_1.ethers.utils.parseUnits('1', sourceToken.decimals);
            rateBN = amountOut.mul(oneSourceBN).div(amountIn);
        }
        const rate = Number(ethers_1.ethers.utils.formatUnits(rateBN, destToken.decimals));
        return rate;
    }
    getPriceImpact(rate, marketRate) {
        return ((marketRate - rate) / marketRate) * 100;
    }
    async checkConnectedChain(signer, chain) {
        const connectedChainId = await (signer === null || signer === void 0 ? void 0 : signer.getChainId());
        if (connectedChainId !== chain.chainId) {
            throw new Error(`invalid connected chain ID "${connectedChainId}". Make sure web3 signer provider is connected to source chain from network "${chain.slug}" chain ID "${chain.chainId}"`);
        }
    }
    // Gnosis AMB bridge
    async getAmbBridge(chain) {
        chain = this.toChainModel(chain);
        if (chain.equals(Chain_1.default.Ethereum)) {
            const address = this.getL1AmbBridgeAddress(this.tokenSymbol, Chain_1.default.Gnosis);
            const provider = await this.getSignerOrProvider(Chain_1.default.Ethereum);
            return L1_HomeAMBNativeToErc20__factory_1.L1_HomeAMBNativeToErc20__factory.connect(address, provider);
        }
        const address = this.getL2AmbBridgeAddress(this.tokenSymbol, Chain_1.default.Gnosis);
        const provider = await this.getSignerOrProvider(Chain_1.default.Gnosis);
        return L1_HomeAMBNativeToErc20__factory_1.L1_HomeAMBNativeToErc20__factory.connect(address, provider);
    }
    getChainNativeToken(chain) {
        chain = this.toChainModel(chain);
        if (chain === null || chain === void 0 ? void 0 : chain.equals(Chain_1.default.Polygon)) {
            return this.toTokenModel(constants_1.CanonicalToken.MATIC);
        }
        else if (chain === null || chain === void 0 ? void 0 : chain.equals(Chain_1.default.Gnosis)) {
            return this.toTokenModel(constants_1.CanonicalToken.DAI);
        }
        return this.toTokenModel(constants_1.CanonicalToken.ETH);
    }
    isNativeToken(chain) {
        const token = this.getCanonicalToken(chain || this.sourceChain);
        return token.isNativeToken;
    }
    async getEthBalance(chain = this.sourceChain, address) {
        chain = this.toChainModel(chain);
        address = address !== null && address !== void 0 ? address : await this.getSignerAddress();
        if (!address) {
            throw new Error('address is required');
        }
        return chain.provider.getBalance(address);
    }
    isSupportedAsset(chain) {
        var _a, _b;
        try {
            chain = this.toChainModel(chain);
            const supported = this.getSupportedAssets();
            const token = this.toTokenModel(this.tokenSymbol);
            return (_b = !!((_a = supported[chain === null || chain === void 0 ? void 0 : chain.slug]) === null || _a === void 0 ? void 0 : _a[token.canonicalSymbol])) !== null && _b !== void 0 ? _b : false;
        }
        catch (err) {
            console.error(err);
        }
        return false;
    }
    async getBonderAddress(sourceChain, destinationChain) {
        return await this._getBonderAddress(this.tokenSymbol, sourceChain, destinationChain);
    }
    async getMessengerWrapperAddress(destinationChain) {
        return await this._getMessengerWrapperAddress(this.tokenSymbol, destinationChain);
    }
    shouldAttemptSwap(amountOutMin, deadline) {
        if (!this.doesUseAmm) {
            return false;
        }
        deadline = ethers_1.BigNumber.from((deadline === null || deadline === void 0 ? void 0 : deadline.toString()) || 0);
        return (amountOutMin === null || amountOutMin === void 0 ? void 0 : amountOutMin.gt(0)) || (deadline === null || deadline === void 0 ? void 0 : deadline.gt(0));
    }
    async getGasEstimateFromAddress(sourceChain, destinationChain) {
        let address = await this.getSignerAddress();
        if (!address) {
            address = await this.getBonderAddress(sourceChain, destinationChain);
        }
        return address;
    }
    async withdraw(chain, recipient, amount, transferNonce, bonderFee, amountOutMin, deadline, transferRootHash, rootTotalAmount, transferIdTreeIndex, siblings, totalLeaves) {
        chain = this.toChainModel(chain);
        await this.checkConnectedChain(this.signer, chain);
        const populatedTx = await this.populateWithdrawTx(chain, recipient, amount, transferNonce, bonderFee, amountOutMin, deadline, transferRootHash, rootTotalAmount, transferIdTreeIndex, siblings, totalLeaves);
        return this.sendTransaction(populatedTx, chain);
    }
    async populateWithdrawTx(chain, recipient, amount, transferNonce, bonderFee, amountOutMin, deadline, transferRootHash, rootTotalAmount, transferIdTreeIndex, siblings, totalLeaves) {
        chain = this.toChainModel(chain);
        const txOptions = await this.txOverrides(chain);
        const args = [
            recipient,
            amount,
            transferNonce,
            bonderFee,
            amountOutMin,
            deadline,
            transferRootHash,
            rootTotalAmount,
            transferIdTreeIndex,
            siblings,
            totalLeaves,
            txOptions
        ];
        const bridge = await this.getBridgeContract(chain);
        return bridge.populateTransaction.withdraw(...args);
    }
    async populateWithdrawTransferTx(sourceChain, destinationChain, transferIdOrTransactionHash) {
        sourceChain = this.toChainModel(sourceChain);
        const wp = new WithdrawalProof_1.WithdrawalProof(this.network, transferIdOrTransactionHash);
        await wp.generateProof();
        await wp.checkWithdrawable();
        const { recipient, amount, transferNonce, bonderFee, amountOutMin, deadline, transferRootHash, rootTotalAmount, transferIdTreeIndex, siblings, totalLeaves } = wp.getTxPayload();
        return this.populateWithdrawTx(wp.transfer.destinationChain, recipient, amount, transferNonce, bonderFee, amountOutMin, deadline, transferRootHash, rootTotalAmount, transferIdTreeIndex, siblings, totalLeaves);
    }
    async withdrawTransfer(sourceChain, destinationChain, transferIdOrTransactionHash) {
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        const populatedTx = await this.populateWithdrawTransferTx(sourceChain, destinationChain, transferIdOrTransactionHash);
        return this.sendTransaction(populatedTx, destinationChain);
    }
    async getWithdrawProof(sourceChain, destinationChain, transferIdOrTransactionHash) {
        sourceChain = this.toChainModel(sourceChain);
        const wp = new WithdrawalProof_1.WithdrawalProof(this.network, transferIdOrTransactionHash);
        return wp.generateProof();
    }
    setPriceFeedApiKeys(apiKeys = {}) {
        this.priceFeedApiKeys = apiKeys;
        this.priceFeed.setApiKeys(this.priceFeedApiKeys);
    }
    async needsApproval(amount, sourceChain, address, destinationChain // this param was later added hence it's after the address param for backward compatibility
    ) {
        const token = this.getCanonicalToken(sourceChain);
        const isHTokenTransfer = false;
        const spender = this.getSendApprovalAddress(sourceChain, isHTokenTransfer, destinationChain);
        return token.needsApproval(spender, amount, address);
    }
    async needsHTokenApproval(amount, sourceChain, address, destinationChain // this param was later added hence it's after the address param for backward compatibility
    ) {
        const token = this.getCanonicalToken(sourceChain);
        const isHTokenTransfer = true;
        const spender = this.getSendApprovalAddress(sourceChain, isHTokenTransfer, destinationChain);
        return token.needsApproval(spender, amount, address);
    }
    parseUnits(value, decimals) {
        value = value.toString() || '0';
        const token = this.toTokenModel(this.tokenSymbol);
        return (0, utils_1.parseUnits)(value, decimals !== null && decimals !== void 0 ? decimals : token.decimals);
    }
    formatUnits(value, decimals) {
        value = value.toString() || '0';
        const token = this.toTokenModel(this.tokenSymbol);
        return Number((0, utils_1.formatUnits)(value, decimals !== null && decimals !== void 0 ? decimals : token.decimals));
    }
    calcAmountOutMin(amountOut, slippageTolerance) {
        amountOut = ethers_1.BigNumber.from(amountOut.toString());
        const slippageToleranceBps = slippageTolerance * 100;
        const minBps = Math.ceil(10000 - slippageToleranceBps);
        return amountOut.mul(minBps).div(10000);
    }
    async isDestinationChainPaused(destinationChain) {
        destinationChain = this.toChainModel(destinationChain);
        const l1Bridge = await this.getL1Bridge();
        const isPaused = await l1Bridge.isChainIdPaused(destinationChain.chainId);
        return isPaused;
    }
    // chains that the asset supports
    get supportedChains() {
        const supported = new Set();
        const token = this.toTokenModel(this.tokenSymbol);
        for (const chain in this.chains) {
            if (this.addresses[token.canonicalSymbol][chain]) {
                supported.add(chain);
            }
        }
        return Array.from(supported);
    }
    // L2 chains that the asset supports to LP
    get supportedLpChains() {
        const token = this.toTokenModel(this.tokenSymbol);
        const supported = new Set();
        const nonAmmAssets = this.getNonAmmAssets();
        for (const chain of this.supportedChains) {
            if (chain === constants_1.ChainSlug.Ethereum || token.canonicalSymbol === Token_2.default.HOP) {
                continue;
            }
            if (nonAmmAssets.has(token.canonicalSymbol)) {
                continue;
            }
            supported.add(chain);
        }
        return Array.from(supported);
    }
    getSupportedLpChains() {
        return this.supportedLpChains;
    }
    getNonAmmAssets() {
        const list = new Set([constants_1.CanonicalToken.HOP]);
        if (this.network === constants_1.NetworkSlug.Goerli) {
            list.add(constants_1.CanonicalToken.USDT);
            list.add(constants_1.CanonicalToken.DAI);
            list.add(constants_1.CanonicalToken.UNI);
        }
        return list;
    }
    async getAccountLpBalance(chain, account) {
        const lpToken = this.getSaddleLpToken(chain);
        const balance = await lpToken.balanceOf(account);
        return balance;
    }
    async getAccountLpCanonicalBalance(chain, account) {
        const token = this.toTokenModel(this.tokenSymbol);
        const lpToken = this.getSaddleLpToken(chain);
        const balance = await lpToken.balanceOf(account);
        const amm = this.getAmm(chain);
        const virtualPrice = await amm.getVirtualPrice();
        const canonicalBalance = balance.mul(virtualPrice).div((0, utils_1.parseUnits)('1', 18));
        return canonicalBalance;
    }
    async getAccountLpCanonicalBalanceUsd(chain, account) {
        const token = this.toTokenModel(this.tokenSymbol);
        const [balance, tokenPrice] = await Promise.all([
            this.getAccountLpCanonicalBalance(chain, account),
            this.priceFeed.getPriceByTokenSymbol(token.canonicalSymbol)
        ]);
        if (balance.lte(0)) {
            return 0;
        }
        const balanceFormatted = this.formatUnits(balance, 18);
        let balanceUsd = balanceFormatted * tokenPrice;
        if (balanceUsd < 0) {
            balanceUsd = 0;
        }
        return balanceUsd;
    }
    async getPriceByTokenSymbol(tokenSymbol) {
        const timeStart = Date.now();
        const price = await this.priceFeed.getPriceByTokenSymbol(tokenSymbol);
        this.debugTimeLog('getPriceByTokenSymbol', timeStart);
        return price;
    }
    async isValidRelayerAndRelayerFee(relayer, relayerFee) {
        return (relayer !== ethers_1.constants.AddressZero ||
            relayerFee.eq(0));
    }
    // TODO: This is a temporary solution. Should retrieve from onchain and cache value.
    getLpFeeBps(chain) {
        const defaultFeeBps = 4;
        const customFeeBps = {
            [Chain_1.default.PolygonZk.slug]: 1,
            [Chain_1.default.Nova.slug]: 1
        };
        if (customFeeBps[chain.slug]) {
            return ethers_1.BigNumber.from(customFeeBps[chain.slug]);
        }
        return ethers_1.BigNumber.from(defaultFeeBps);
    }
}
exports.default = HopBridge;
